/**
 * useExportToGoogleSheets
 *
 * Returns an `extraExportOptions` entry for the ui-core table dropdown plus a
 * dialog element to render alongside it. The dialog collects a Google Sheets
 * credential and a sheet title, then runs two Composio actions:
 *
 *   1. google_sheets.GOOGLESHEETS_CREATE_GOOGLE_SHEET1     — create the spreadsheet
 *   2. google_sheets.GOOGLESHEETS_SPREADSHEETS_VALUES_APPEND — write headers + rows
 *
 * On success, toasts with a link to the new sheet.
 */

import { useState, useMemo, useCallback } from 'react';
import { FileSpreadsheet, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
  type TableExportOption,
} from '@strivelabs/ui-core';
import { Button } from '@strivelabs/ui-core/v2';
import { CredentialPicker } from '@/components/agent';
import { useCredentials } from '@/hooks/useConnections';
import { BASE_URL, executeAppAction } from '@/services/DataService';
import type { ConnectionsCardData } from '@/types/connections';
import { log } from '@/lib/logger';

const GOOGLE_SHEETS_APP_NAME = 'google_sheets';
const ACTION_CREATE_SHEET = 'GOOGLESHEETS_CREATE_GOOGLE_SHEET1';
const ACTION_APPEND_VALUES = 'GOOGLESHEETS_SPREADSHEETS_VALUES_APPEND';

interface PendingExport {
  headers: string[];
  rows: string[][];
}

/**
 * Tries a few common shapes Composio returns for the create-sheet action.
 * The Sheets API itself returns { spreadsheetId, spreadsheetUrl, sheets: [{ properties: { title } }], ... }.
 */
function extractCreatedSheet(result: unknown): { spreadsheetId: string; spreadsheetUrl: string; sheetTitle?: string } | null {
  // executeAppAction returns { items: [{ data: <composio response> }] }
  const data = (result as { items?: Array<{ data?: unknown }> })?.items?.[0]?.data;
  if (!data || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;
  // Composio typically nests under `response_data` or returns flat
  const candidate = (obj.response_data as Record<string, unknown>) ?? obj;
  const spreadsheetId = candidate.spreadsheetId ?? candidate.spreadsheet_id;
  const spreadsheetUrl = candidate.spreadsheetUrl ?? candidate.spreadsheet_url;
  if (typeof spreadsheetId !== 'string' || typeof spreadsheetUrl !== 'string') {
    return null;
  }
  // The first sheet's title is locale-dependent ("Sheet1" / "Feuille1" / "Hoja1" / …).
  // Read it from the response so the subsequent values.append targets the right tab.
  const sheets = candidate.sheets as Array<{ properties?: { title?: unknown } }> | undefined;
  const firstTitle = sheets?.[0]?.properties?.title;
  const sheetTitle = typeof firstTitle === 'string' ? firstTitle : undefined;
  return { spreadsheetId, spreadsheetUrl, sheetTitle };
}

export function useExportToGoogleSheets(defaultTitle?: string) {
  const [isOpen, setIsOpen] = useState(false);
  const [pending, setPending] = useState<PendingExport | null>(null);
  const [credentialId, setCredentialId] = useState<string>('');
  const [title, setTitle] = useState<string>(defaultTitle ?? 'Exported table');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Gate the export option behind a connected google_sheets credential.
  // The dropdown entry is only surfaced when the user has at least one
  // google_sheets connection.
  const { data: credentialsData } = useCredentials();
  const googleSheetsCredentials = useMemo(
    () => (credentialsData?.credentials ?? []).filter(
      (cred: ConnectionsCardData) => cred.app_name === GOOGLE_SHEETS_APP_NAME,
    ),
    [credentialsData],
  );

  const exportOption = useMemo<TableExportOption | null>(() => {
    if (googleSheetsCredentials.length === 0) return null;
    return {
      label: 'Export to Google Sheets',
      icon: <FileSpreadsheet className="h-4 w-4" />,
      onSelect: (data) => {
        setPending(data);
        setTitle(defaultTitle ?? 'Exported table');
        // Auto-select when exactly one google_sheets credential exists; otherwise let the user pick.
        setCredentialId(googleSheetsCredentials.length === 1 ? googleSheetsCredentials[0].id : '');
        setIsOpen(true);
      },
    };
  }, [defaultTitle, googleSheetsCredentials]);

  const close = useCallback(() => {
    if (isSubmitting) return;
    setIsOpen(false);
    setPending(null);
  }, [isSubmitting]);

  const handleSubmit = useCallback(async () => {
    if (!pending || !credentialId || !title.trim()) return;

    setIsSubmitting(true);
    // Captured between the create and append calls so a partial-success failure
    // (sheet created but append failed) can still surface a recovery link.
    let createdSheetUrl: string | null = null;
    try {
      const createResult = await executeAppAction(
        BASE_URL,
        GOOGLE_SHEETS_APP_NAME,
        ACTION_CREATE_SHEET,
        [{ id: credentialId }],
        [{ title: title.trim() }],
      );

      const sheet = extractCreatedSheet(createResult);
      if (!sheet) {
        throw new Error('Could not parse spreadsheet URL from response');
      }
      createdSheetUrl = sheet.spreadsheetUrl;

      await executeAppAction(
        BASE_URL,
        GOOGLE_SHEETS_APP_NAME,
        ACTION_APPEND_VALUES,
        [{ id: credentialId }],
        [{
          spreadsheetId: sheet.spreadsheetId,
          range: `${sheet.sheetTitle ?? 'Sheet1'}!A1`,
          values: [pending.headers, ...pending.rows],
          valueInputOption: 'USER_ENTERED',
        }],
      );

      toast({
        title: 'Sheet created',
        description: 'Your data has been exported to a new Google Sheet.',
        action: (
          <a
            href={sheet.spreadsheetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:underline"
          >
            Open <ExternalLink className="h-3 w-3" />
          </a>
        ),
      });
      setIsOpen(false);
      setPending(null);
    } catch (err) {
      log('error', 'useExportToGoogleSheets: failed to export', { error: err });
      const baseDescription = err instanceof Error ? err.message : 'Unknown error';
      toast({
        title: 'Failed to export to Google Sheets',
        description: createdSheetUrl
          ? `${baseDescription}. The spreadsheet was created but the data could not be written — open it to retry manually.`
          : baseDescription,
        variant: 'destructive',
        action: createdSheetUrl ? (
          <a
            href={createdSheetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:underline"
          >
            Open <ExternalLink className="h-3 w-3" />
          </a>
        ) : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [pending, credentialId, title]);

  const dialog = (
    <Dialog open={isOpen} onOpenChange={(open) => (open ? setIsOpen(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Export to Google Sheets</DialogTitle>
          <DialogDescription>
            Select a connected Google Sheets account and pick a name for the new spreadsheet.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gs-export-credential">Connection</Label>
            <CredentialPicker
              value={credentialId}
              onValueChange={setCredentialId}
              appName={GOOGLE_SHEETS_APP_NAME}
              placeholder="Select a Google Sheets connection"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gs-export-title">Sheet name</Label>
            <Input
              id="gs-export-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Exported table"
              disabled={isSubmitting}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={close} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!credentialId || !title.trim() || isSubmitting}
          >
            {isSubmitting ? 'Exporting…' : 'Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { exportOption, dialog };
}
